'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type ReactElement } from 'react';
import { Menu } from '@base-ui/react/menu';
import { clsx } from 'clsx';
import styles from './Filters.module.css';

// ==================== TYPES ====================

export interface Filter<T = unknown> {
  id: string;
  field: string;
  operator: string;
  values: T[];
}

export interface FilterOption<T = unknown> {
  value: T;
  label: string;
  icon?: ReactNode;
  className?: string;
}

export interface FilterOperator {
  value: string;
  label: string;
}

export interface FilterFieldConfig<T = unknown> {
  key: string;
  label: string;
  type?: 'text' | 'select' | 'multiselect';
  icon?: ReactNode;
  options?: FilterOption<T>[];
  operators?: FilterOperator[];
  defaultOperator?: string;
  placeholder?: string;
  searchable?: boolean;
  maxSelections?: number;
  className?: string;
}

export interface FilterFieldGroup<T = unknown> {
  group: string;
  fields: FilterFieldConfig<T>[];
}

export type FilterFieldsInput<T = unknown> = FilterFieldConfig<T>[] | FilterFieldGroup<T>[];

export interface FiltersProps<T = unknown> {
  filters: Filter<T>[];
  fields: FilterFieldsInput<T>;
  onChange: (filters: Filter<T>[]) => void;
  trigger?: ReactNode;
  allowMultiple?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
  enableShortcut?: boolean;
  shortcutKey?: string;
}

// ==================== HELPERS ====================

export const createFilter = <T = unknown>(
  field: string,
  operator?: string,
  values: T[] = []
): Filter<T> => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  field,
  operator: operator ?? 'is',
  values,
});

function isGrouped<T>(fields: FilterFieldsInput<T>): fields is FilterFieldGroup<T>[] {
  return fields.length > 0 && 'group' in fields[0];
}

function flattenFields<T>(fields: FilterFieldsInput<T>): FilterFieldConfig<T>[] {
  if (isGrouped(fields)) return fields.flatMap(g => g.fields);
  return fields as FilterFieldConfig<T>[];
}

const TEXT_OPERATORS: FilterOperator[] = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is', label: 'is exactly' },
  { value: 'starts_with', label: 'starts with' },
];

const SELECT_OPERATORS: FilterOperator[] = [
  { value: 'is', label: 'is' },
  { value: 'is_not', label: 'is not' },
];

const MULTISELECT_OPERATORS: FilterOperator[] = [
  { value: 'is_any_of', label: 'is any of' },
  { value: 'is_not_any_of', label: 'is not any of' },
];

function getOperators(field: FilterFieldConfig): FilterOperator[] {
  if (field.operators) return field.operators;
  if (field.type === 'multiselect') return MULTISELECT_OPERATORS;
  if (field.type === 'select') return SELECT_OPERATORS;
  return TEXT_OPERATORS;
}

function getDefaultOperator(field: FilterFieldConfig): string {
  if (field.defaultOperator) return field.defaultOperator;
  if (field.type === 'multiselect') return 'is_any_of';
  if (field.type === 'select') return 'is';
  return 'contains';
}

// ==================== ICONS ====================

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14" aria-hidden>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="12" height="12" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// ==================== OPERATOR DROPDOWN ====================

interface FilterOperatorDropdownProps {
  field: FilterFieldConfig;
  operator: string;
  onChange: (op: string) => void;
}

function FilterOperatorDropdown({ field, operator, onChange }: FilterOperatorDropdownProps) {
  const operators = getOperators(field);
  const label = operators.find(op => op.value === operator)?.label ?? operator;

  return (
    <Menu.Root>
      <Menu.Trigger className={styles.segment}>
        {label}
        <ChevronDownIcon />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="start" sideOffset={4}>
          <Menu.Popup className={styles.popup}>
            {operators.map(op => (
              <Menu.Item
                key={op.value}
                className={clsx(styles.item, op.value === operator && styles.itemActive)}
                onClick={() => onChange(op.value)}
              >
                {op.label}
                {op.value === operator && <CheckIcon />}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

// ==================== VALUE SELECTOR ====================

interface FilterValueSelectorProps<T = unknown> {
  field: FilterFieldConfig<T>;
  values: T[];
  operator: string;
  onChange: (values: T[]) => void;
}

function FilterValueSelector<T>({ field, values, onChange }: FilterValueSelectorProps<T>) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  if (field.type === 'text') {
    return (
      <input
        className={styles.segmentInput}
        type="text"
        value={(values[0] as string) ?? ''}
        placeholder={field.placeholder ?? `${field.label}...`}
        onChange={e => onChange([e.target.value as T])}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  const isMulti = field.type === 'multiselect';
  const selectedOptions = field.options?.filter(o => values.includes(o.value)) ?? [];
  const displayLabel =
    selectedOptions.length === 0
      ? 'Select...'
      : selectedOptions.length === 1
      ? selectedOptions[0].label
      : `${selectedOptions.length} selected`;

  const visibleOptions = search
    ? (field.options ?? []).filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : field.options ?? [];

  return (
    <Menu.Root onOpenChange={open => { if (!open) setTimeout(() => setSearch(''), 150); }}>
      <Menu.Trigger className={styles.segment}>
        {selectedOptions.length > 0 && (
          <span className={styles.valueIcons}>
            {selectedOptions.slice(0, 3).map(o => o.icon && <span key={String(o.value)}>{o.icon}</span>)}
          </span>
        )}
        {displayLabel}
        <ChevronDownIcon />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="start" sideOffset={4}>
          <Menu.Popup className={clsx(styles.popup, styles.valuePopup, field.className)}>
            {field.searchable !== false && (
              <>
                <div className={styles.searchRow}>
                  <input
                    ref={inputRef}
                    className={styles.searchInput}
                    placeholder={`Search ${field.label.toLowerCase()}...`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                  />
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.optionsList}>
              {visibleOptions.length === 0 && (
                <div className={styles.empty}>No results</div>
              )}
              {visibleOptions.map(option => {
                const isSelected = values.includes(option.value);
                return (
                  <Menu.Item
                    key={String(option.value)}
                    className={clsx(styles.item, option.className)}
                    closeOnClick={!isMulti}
                    onClick={() => {
                      if (isMulti) {
                        onChange(
                          isSelected
                            ? values.filter(v => v !== option.value)
                            : [...values, option.value]
                        );
                      } else {
                        onChange([option.value]);
                      }
                    }}
                  >
                    {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                    <span>{option.label}</span>
                    {isSelected && <span className={styles.checkmark}><CheckIcon /></span>}
                  </Menu.Item>
                );
              })}
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

// ==================== MAIN COMPONENT ====================

export function Filters<T = unknown>({
  filters,
  fields,
  onChange,
  trigger,
  allowMultiple = true,
  size = 'default',
  className,
  enableShortcut = false,
  shortcutKey,
}: FiltersProps<T>) {
  const [menuSearch, setMenuSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const flatFields = useMemo(() => flattenFields(fields), [fields]);

  const fieldsMap = useMemo(
    () => Object.fromEntries(flatFields.map(f => [f.key, f])),
    [flatFields]
  );

  const selectableFields = useMemo(
    () => allowMultiple
      ? flatFields
      : flatFields.filter(f => !filters.some(filter => filter.field === f.key)),
    [flatFields, filters, allowMultiple]
  );

  // For grouped mode: filter groups down by search + selectability
  const filteredGroups = useMemo(() => {
    if (!isGrouped(fields)) return [];
    const selectableKeys = new Set(selectableFields.map(f => f.key));
    return fields
      .map(group => ({
        ...group,
        fields: group.fields.filter(f =>
          selectableKeys.has(f.key) &&
          (!menuSearch || f.label.toLowerCase().includes(menuSearch.toLowerCase()))
        ),
      }))
      .filter(g => g.fields.length > 0);
  }, [fields, selectableFields, menuSearch]);

  // For flat mode: filter selectableFields by search
  const filteredFlatFields = useMemo(
    () => menuSearch
      ? selectableFields.filter(f => f.label.toLowerCase().includes(menuSearch.toLowerCase()))
      : selectableFields,
    [selectableFields, menuSearch]
  );

  const addFilter = useCallback((fieldKey: string) => {
    const field = flatFields.find(f => f.key === fieldKey);
    if (!field) return;
    const defaultValues: T[] = field.type === 'text' ? ['' as T] : [];
    onChange([...filters, createFilter<T>(fieldKey, getDefaultOperator(field as FilterFieldConfig), defaultValues)]);
  }, [flatFields, filters, onChange]);

  const updateFilter = useCallback((id: string, updates: Partial<Filter<T>>) => {
    onChange(filters.map(f => f.id === id ? { ...f, ...updates } : f));
  }, [filters, onChange]);

  const removeFilter = useCallback((id: string) => {
    onChange(filters.filter(f => f.id !== id));
  }, [filters, onChange]);

  // Keyboard shortcut to open the add-filter menu
  useEffect(() => {
    if (!enableShortcut || !shortcutKey) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
      if (e.key.toLowerCase() === shortcutKey.toLowerCase() && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setMenuOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enableShortcut, shortcutKey]);

  const grouped = isGrouped(fields);
  const hasSelectableFields = selectableFields.length > 0;
  const hasResults = grouped
    ? filteredGroups.length > 0
    : filteredFlatFields.length > 0;

  return (
    <div className={clsx(styles.root, className)}>
      {hasSelectableFields && (
        <Menu.Root
          open={menuOpen}
          onOpenChange={open => {
            setMenuOpen(open);
            if (!open) setTimeout(() => setMenuSearch(''), 150);
          }}
        >
          {trigger ? (
            <Menu.Trigger render={trigger as ReactElement} />
          ) : (
            <Menu.Trigger className={clsx(styles.addBtn, styles[size])}>
              <FilterIcon />
              Filter
              {enableShortcut && shortcutKey && (
                <span className={styles.shortcutBadge}>{shortcutKey.toUpperCase()}</span>
              )}
            </Menu.Trigger>
          )}
          <Menu.Portal>
            <Menu.Positioner side="bottom" align="start" sideOffset={4}>
              <Menu.Popup className={styles.popup} style={{ width: 220 }}>
                <div className={styles.searchRow}>
                  <input
                    className={styles.searchInput}
                    placeholder="Filter..."
                    value={menuSearch}
                    onChange={e => setMenuSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                  />
                </div>
                <div className={styles.divider} />
                <div className={styles.optionsList}>
                  {!hasResults && <div className={styles.empty}>No filters found</div>}
                  {grouped
                    ? filteredGroups.map((group, groupIdx) => (
                        <div key={group.group}>
                          {groupIdx > 0 && <div className={styles.groupDivider} />}
                          <div className={styles.groupLabel}>{group.group}</div>
                          {group.fields.map(field => (
                            <Menu.Item
                              key={field.key}
                              className={styles.item}
                              onClick={() => addFilter(field.key)}
                            >
                              {field.icon && <span className={styles.optionIcon}>{field.icon}</span>}
                              {field.label}
                            </Menu.Item>
                          ))}
                        </div>
                      ))
                    : filteredFlatFields.map(field => (
                        <Menu.Item
                          key={field.key}
                          className={styles.item}
                          onClick={() => addFilter(field.key)}
                        >
                          {field.icon && <span className={styles.optionIcon}>{field.icon}</span>}
                          {field.label}
                        </Menu.Item>
                      ))
                  }
                </div>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      )}

      {filters.map(filter => {
        const field = fieldsMap[filter.field] as FilterFieldConfig<T> | undefined;
        if (!field) return null;
        return (
          <div key={filter.id} className={clsx(styles.chip, styles[size])}>
            <span className={styles.chipLabel}>
              {field.icon && <span className={styles.optionIcon}>{field.icon}</span>}
              {field.label}
            </span>
            <FilterOperatorDropdown
              field={field as FilterFieldConfig}
              operator={filter.operator}
              onChange={op => updateFilter(filter.id, { operator: op })}
            />
            <FilterValueSelector
              field={field}
              values={filter.values}
              operator={filter.operator}
              onChange={vals => updateFilter(filter.id, { values: vals })}
            />
            <button
              type="button"
              className={styles.chipRemove}
              onClick={() => removeFilter(filter.id)}
              aria-label={`Remove ${field.label} filter`}
            >
              <XIcon />
            </button>
          </div>
        );
      })}
    </div>
  );
}
